import { useLocation, useNavigate } from 'react-router-dom';

import { Header } from '@/components/Header';
import { PageContainer } from '@/components/PageContainer';
import { ClaimsTab } from '@/components/admin/ClaimsTab';
import { UsersTab } from '@/components/admin/UsersTab';

import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

import { ADMIN_CLAIM_UI, ADMIN_USERS_UI, isFeatureEnabled } from '@/utils/feature-flags';

type TabType = 'overview' | 'claims' | 'users' | 'export';

const TABS = [
  { id: 'overview', label: 'Overview', path: '/admin/dashboard' },
  { id: 'claims', label: 'Claim Management', path: '/admin/claims' },
  { id: 'users', label: 'User Management', path: '/admin/users' },
  { id: 'export', label: 'Export', path: '/admin/export' },
];

function AdminDashboard() {
  const { signOut, user } = useAuth();
  const { data: featureFlags } = useFeatureFlags();
  const adminClaimUiEnabled = isFeatureEnabled(featureFlags, ADMIN_CLAIM_UI);
  const adminUsersUiEnabled = isFeatureEnabled(featureFlags, ADMIN_USERS_UI);
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTabFromPath = (): TabType => {
    const currentTab = TABS.find((tab) => location.pathname.startsWith(tab.path));
    return (currentTab?.id as TabType) ?? 'overview';
  };

  const activeTab = getActiveTabFromPath();

  if (!user) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header user={user} onSignOut={signOut} />
      <PageContainer maxWidth="7xl" className="flex flex-1 flex-col">
        <header className="mb-4">
          <h1 className="mb-8 text-center text-3xl font-bold text-gray-900">Unleash Admin</h1>
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {TABS.map((tab) => (
                <button
                  key={tab.label}
                  data-testid={`admin-tab-${tab.id}`}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                  onClick={() => navigate(tab.path)}
                  className={`border-b-2 px-1 py-4 text-sm font-medium ${
                    activeTab === tab.id
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </header>

        <div className="flex flex-1 flex-col space-y-6">
          {activeTab === 'claims' &&
            (adminClaimUiEnabled ? (
              <ClaimsTab />
            ) : (
              <div className="text-sm text-gray-500">Claim Management coming soon.</div>
            ))}
          {activeTab === 'overview' && (
            <div className="text-sm text-gray-500">Overview coming soon.</div>
          )}
          {activeTab === 'users' &&
            (adminUsersUiEnabled ? (
              <UsersTab />
            ) : (
              <div className="text-sm text-gray-500">User Management coming soon.</div>
            ))}
          {activeTab === 'export' && (
            <div className="text-sm text-gray-500">Export coming soon.</div>
          )}
        </div>
      </PageContainer>
    </div>
  );
}

export { AdminDashboard };
